import React from 'react';
import { Pressable, Text, View, StyleSheet } from 'react-native';
import { Feather } from '@expo/vector-icons';
import { useTranslation } from 'react-i18next';
import { Paprika } from '../../../../styles/theme';
import { RemoteImage } from '../../../shared/RemoteImage';
import type { SideOption } from '../useSideSuggestions';

interface Props {
  /** The accompaniment to name. Null draws the plain « + Accompagnement » row. */
  side: SideOption | null;
  /**
   * Floats under the card instead of taking a line of the grid: the offer
   * appears on hover, and a row that grew under the cursor would push the
   * meals below it down and back up as the pointer crossed the week.
   */
  floating: boolean;
  onAdd: () => void;
  onMore: () => void;
}

/**
 * What a desktop cell offers to serve with its dish (#746). The grid was built
 * one dish per slot: every way of putting a recipe in an occupied cell replaced
 * what was there, so a side could only be added from the recipe's own page.
 * The row names one accompaniment — a click adds it — and the chevron opens the
 * rest.
 */
export function SlotSideOffer({ side, floating, onAdd, onMore }: Props) {
  const { t } = useTranslation();

  return (
    <View style={floating ? styles.floating : undefined} testID="slot-side-offer">
      <View style={[styles.row, floating && styles.rowFloating]}>
        <Pressable
          style={styles.body}
          onPress={side ? onAdd : onMore}
          accessibilityRole="button"
          accessibilityLabel={side ? `${t('thisWeek.addSideLong')} — ${side.recipe.title}` : t('thisWeek.addSideLong')}
          testID="slot-add-side"
        >
          {side ? (
            <RemoteImage
              uri={side.recipe.image}
              variant="thumb"
              style={styles.thumb}
              resizeMode="cover"
              fallback={<View style={[styles.thumb, styles.thumbPlaceholder]} />}
            />
          ) : (
            <Feather name="plus" size={12} color={Paprika.primary} />
          )}
          <View style={styles.text}>
            {side ? (
              <>
                <Text style={styles.kicker} numberOfLines={1}>+ {t('thisWeek.addSide').toUpperCase()}</Text>
                <Text style={styles.title} numberOfLines={1} testID="slot-side-title">{side.recipe.title}</Text>
              </>
            ) : (
              <Text style={styles.plain} numberOfLines={1}>{t('thisWeek.addSideLong')}</Text>
            )}
          </View>
        </Pressable>
        {side && (
          <Pressable
            style={styles.more}
            onPress={onMore}
            accessibilityRole="button"
            accessibilityLabel={t('thisWeek.moreSidesLong')}
            testID="slot-more-sides"
          >
            <Feather name="chevron-right" size={14} color={Paprika.primary} />
          </Pressable>
        )}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  // Sits flush under the cell so the pointer never leaves the cell on its way
  // down; the 4px of air is padding, inside the hover target.
  floating: {
    position: 'absolute',
    top: '100%' as any,
    left: 0,
    right: 0,
    paddingTop: 4,
    zIndex: 5,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    borderRadius: 10,
    backgroundColor: Paprika.brandTint,
  },
  rowFloating: {
    boxShadow: '0 6px 16px rgba(82, 68, 60, 0.14)',
  } as any,
  body: {
    flex: 1,
    minWidth: 0,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 7,
    paddingVertical: 5,
    paddingLeft: 6,
  },
  thumb: {
    width: 26,
    height: 26,
    borderRadius: 6,
    flexShrink: 0,
    backgroundColor: Paprika.surfaceLow,
  },
  thumbPlaceholder: {
    backgroundColor: Paprika.bgSunk,
  },
  text: {
    flex: 1,
    minWidth: 0,
  },
  kicker: {
    fontFamily: 'Inter-SemiBold',
    fontSize: 9,
    letterSpacing: 0.5,
    color: Paprika.primary,
  },
  title: {
    fontFamily: 'Newsreader-Italic',
    fontSize: 12,
    lineHeight: 15,
    color: Paprika.textPrimary,
  },
  plain: {
    fontFamily: 'Inter-Medium',
    fontSize: 11,
    color: Paprika.primary,
  },
  more: {
    alignSelf: 'stretch',
    justifyContent: 'center',
    paddingHorizontal: 6,
  },
});
